var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useEffect, useRef, useCallback } from 'react';
import classNames from 'classnames';
import { Close, Success, Warning, WarningTriangle } from '../../foundations/icons';
import { Typography } from '../../foundations/typography';
import { IconButton } from '../../core/IconButton';
import { TextLink } from '../TextLink';
import { logDevelopmentMessage } from '../../utils/logDevelopmentMessage';
import styles from './Snackbar.module.scss';
/**
 * @tdr
 */
export var Snackbar = function (_a) {
    var _b;
    var action = _a.action, _c = _a.autoHide, autoHide = _c === void 0 ? true : _c, children = _a.children, _d = _a.closeOnClick, closeOnClick = _d === void 0 ? false : _d, dataTest = _a.dataTest, _e = _a.duration, duration = _e === void 0 ? 6000 : _e, _f = _a.message, message = _f === void 0 ? '' : _f, icon = _a.icon, onClose = _a.onClose, onOpen = _a.onOpen, _g = _a.open, open = _g === void 0 ? false : _g, _h = _a.placement, placement = _h === void 0 ? 'bottom' : _h, _j = _a.snapTo, snapTo = _j === void 0 ? 'window' : _j, _k = _a.type, type = _k === void 0 ? 'info' : _k, props = __rest(_a, ["action", "autoHide", "children", "closeOnClick", "dataTest", "duration", "message", "icon", "onClose", "onOpen", "open", "placement", "snapTo", "type"]);
    if (icon && type !== 'info') {
        logDevelopmentMessage("Snackbar: 'icon' will be overwritten by the 'type' prop. Please specify only one of 'type' or 'icon'.");
    }
    if (message.length > 40) {
        logDevelopmentMessage("Snackbar: 'message' length should not exceed 40 characters.");
    }
    var childrenRef = useRef(null);
    var _l = useState(open), visible = _l[0], setVisible = _l[1];
    var _m = useState(false), closing = _m[0], setClosing = _m[1];
    var autoHideTimeout = useRef();
    var closingDuration = placement === 'topRight' ? 500 : 100; // this should match css animation snackbarDown duration 0.1s
    var _o = action || {}, actionLabel = _o.label, actionHref = _o.href, actionOnClick = _o.onClick;
    var iconByType;
    switch (type) {
        case 'error':
            iconByType = React.createElement(Warning, { "data-testid": "warningIcon" });
            break;
        case 'success':
            iconByType = React.createElement(Success, { "data-testid": "successIcon" });
            break;
        case 'warning':
            iconByType = React.createElement(WarningTriangle, { "data-testid": "warningTriangleIcon" });
            break;
        default:
            iconByType = icon;
            break;
    }
    var handleClose = useCallback(function () {
        setClosing(true);
        autoHideTimeout.current = setTimeout(function () {
            setVisible(false);
            setClosing(false);
            clearTimeout(Number(autoHideTimeout.current));
            if (onClose) {
                onClose();
            }
        }, closingDuration);
    }, [closingDuration, onClose]);
    var handleSnackbarClick = useCallback(function () {
        if (closeOnClick) {
            handleClose();
        }
    }, [closeOnClick, handleClose]);
    var startAutoHideTimeout = useCallback(function () {
        if (autoHide) {
            autoHideTimeout.current = setTimeout(function () {
                handleClose();
            }, duration - closingDuration);
        }
    }, [autoHide, closingDuration, duration, handleClose]);
    var stopAutoHideTimout = useCallback(function () {
        if (autoHide) {
            clearTimeout(Number(autoHideTimeout.current));
        }
    }, [autoHide]);
    useEffect(function () {
        if (open) {
            setVisible(true);
            if (autoHide) {
                startAutoHideTimeout();
            }
            if (onOpen) {
                onOpen();
            }
        }
        return function () {
            clearTimeout(Number(autoHideTimeout.current));
        };
    }, [open, startAutoHideTimeout, onOpen, autoHide]);
    useEffect(function () {
        var _a, _b, _c;
        if (((_a = childrenRef.current) === null || _a === void 0 ? void 0 : _a.textContent) && ((_c = (_b = childrenRef.current) === null || _b === void 0 ? void 0 : _b.textContent) === null || _c === void 0 ? void 0 : _c.length) > 40) {
            logDevelopmentMessage('Snackbar: Text content length should not exceed 40 characters.');
        }
    }, [childrenRef]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "sr-only", role: "status" }, visible ? children || React.createElement("p", null, message) : null),
        visible && (
        // Disabling these eslint rules as screenreader users can use the close button inside the Snakcbar to close it, and the snackbar itself is not even tabbable
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        React.createElement("div", __assign({}, props, { onMouseEnter: stopAutoHideTimout, onMouseLeave: startAutoHideTimeout, onClick: handleSnackbarClick, className: classNames(styles.snackbar, (_b = {},
                _b[styles.closing] = closing,
                _b[styles.containerised] = snapTo === 'parent',
                _b[styles.topRight] = placement === 'topRight',
                _b)), "data-testid": dataTest }),
            !!iconByType && React.createElement("div", { className: styles.icon }, iconByType),
            React.createElement("div", { className: styles.text },
                children ? (React.createElement("div", { ref: childrenRef }, children)) : (React.createElement(Typography, { styleAs: "paragraphHeading", "data-testid": "message", noMargins: true }, message)),
                !!actionLabel && !!actionHref && (React.createElement(TextLink, { className: styles.link, href: actionHref, light: true, onClick: actionOnClick, size: "large" }, actionLabel))),
            !autoHide && (React.createElement(IconButton, { "aria-label": "Dismiss notification", className: styles.close, "data-testid": "closeButton", onClick: handleClose },
                React.createElement(Close, { size: "xsmall" })))))));
};
export default Snackbar;
//# sourceMappingURL=Snackbar.js.map